import * as Actions from '../actions/documentActions';
import Document from '../../entities/document';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type DocumentAction = { 
    type: string; 
    document: Document | null; 
    documentId: string | Document; 
    partial: boolean; 
    lastFetch: number; 
    documents: Document[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Document> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Document[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Document> | null, firmId: string, userId: string, updates?: Document[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'document', 'documents', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Document>} data
 * @param {Document[]} [updates]
 * @returns {ArrayMap<Document>}
 */
function updateStorage(data: ArrayMap<Document>, updates?: Document[]): ArrayMap<Document> {
	if (logging) console.debug('⮂ documentReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Document[]} [updates]
 * @returns {ArrayMap<Document>}
 */
function updateStorageNoReturn(updates?: Document[]): void {
	if (logging) console.debug('⮂ documentReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Document[]} [data]
 * @returns {ArrayMap<Document>}
 */
function createState(data?: Document[] | ArrayMap<Document>): ArrayMap<Document> {
	if (!data) return ArrayMapRegistry.clone<Document>('document', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Document>('document', data.map(e => e));
    else return ArrayMapRegistry.clone<Document>('document', data);
}

function documents(state: ArrayMap<Document>, action: DocumentAction): ArrayMap<Document> {
	if (!state) state = createState();

	let st: ArrayMap<Document>;

	switch (action.type) {
		// action.document - the new value of document with id action.id
		// action.documentId - id of the target, used if new value is null
		case Actions.DOCUMENT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ documentReducer: FETCH_ONE_SUCCESS', action);

			if (action.document === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.documentId)) {
				st = state.clone();
				st.set(action.document);
				// state.clear();

				updateStorageNoReturn([action.document]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.documents
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.documents
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.documents
			 !partial, !stateEmpty, fetchEmpty	-> D return documents in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge documents in state newer than those in action.documents
		 */
		case Actions.DOCUMENT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.documents.length === 0;
			const fetched = action.documents.map(document => new Document({...document, persisted: true}));

			if (logging) console.debug(`⮂ documentReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} documents with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ documentReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ documentReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Document> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Document[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(document => {
					const current = st.get(document.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && document.status === 'D') {
							// Remove it from state
							toUpdate.push(document);
							st.delete(document);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (document.addedOn > current.addedOn) {
								toUpdate.push(document);
								st.set(document);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (document.status !== 'D') {
							toUpdate.push(document);
							st.set(document);
						}
						else if (logging) console.debug('⮂ documentReducer: partialMerge: skipping deleted document not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ documentReducer: empty: documents', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ documentReducer: fullMerge: documents', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localDocument => {
                    // If this document has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localDocument.persisted) {
                        const fetchedDocument = st.get(localDocument.id);
                        if (!fetchedDocument || localDocument.addedOn > fetchedDocument.addedOn) st.set(localDocument);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.DOCUMENT_FETCH_ONE_FAILURE:
			console.error('DOCUMENT_FETCH_ONE_FAILURE', action.documentId, action.err);

			// No update to storage required
			return state;

		case Actions.DOCUMENT_FETCH_FAILURE:
			console.error('DOCUMENT_FETCH_FAILURE', action.documentId, action.err);

			// No update to storage required
			return state;

		case Actions.DOCUMENT_RESTORE: {
		    let document = new Document({...action.document});
            st = state.clone();

            const existing = st.get(document.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (document.addedOn >= existing.addedOn) {
                    st.set(document);
                    return updateStorage(st, [document]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (document.status === 'D') return state;

                st.set(document);
                return updateStorage(st, [document]);
            }
        }
            
		case Actions.DOCUMENT_SAVE: {
		    // console.debug('⮂ DOCUMENT_SAVE reducer', action.document?.id);
		    
			// clone the document
			let document = new Document({...action.document});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new document in the state
			// If this is a create, add to the queue
			const _document = st.get(document.id);

			if (!_document) st.set(document);
			else {
				if (document.addedOn >= _document.addedOn) st.set(document);
			}

			return updateStorage(st, [document]);
		}

		case Actions.DOCUMENT_SAVE_MANY: {
		    if (logging) console.debug('⮂ DOCUMENT_SAVE_MANY reducer', action.documents.map(document => document.id).join(','));
		    
			// clone the documents
			let toProcess = action.documents.map(document => new Document(document));

			st = state.clone();
			// state.clear();

			const updates: Document[] = [];

			toProcess.forEach(document => {
				// Add or update the new document in the state
				// If this is a create, add to the queue
				const existing = st.get(document.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (document.status !== 'D') {
						st.push(document);
						updates.push(document);
					}
					else if (logging) console.debug('⮂ DOCUMENT_SAVE_MANY: skipping deleted document not in state');
				}
				else {
					if (document.addedOn >= existing.addedOn) {
						st.set(document);
						updates.push(document);
					}
					else if (logging) console.debug('⮂ Skipping, existing document had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DOCUMENT_REMOVE: {
			// clone the document
			const document = new Document({...action.document});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(document);

			return updateStorage(st, [document]);
		}

		case Actions.DOCUMENT_RESTORE_FAILURE:
		case Actions.DOCUMENT_SAVE_FAILURE: {
		    console.warn('DOCUMENT_SAVE_FAILURE reducer', action.document?.id);
		    if (!action.document?.id) {
				console.warn('DOCUMENT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const document = ensure(Document, action.document);
			if (!document) {
				console.error('Could not find document after call to save');
				return state;
			}

			st = state.clone();

			const _documentRef = state.get(document.id);

			if (_documentRef && (_documentRef.addedOn === document.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _document = new Document({..._documentRef});
				_document.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_document.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('document', _document).then(r => {
						if (r.isErr()) console.error(`⮂ documentReducer: failed to save document with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ documentReducer: saved document with fatalError info');
					}).catch(err => console.error(`⮂ documentReducer: failed to save document with fatalError info: ${err.message}`));

				st.set(_document);
			}

			return updateStorage(st);
		}

		case Actions.DOCUMENT_SAVE_MANY_FAILURE: {
		    console.warn('DOCUMENT_SAVE_MANY_FAILURE reducer', action.documents.map(document => document.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.DOCUMENT_RESTORE_SUCCESS:
		case Actions.DOCUMENT_SAVE_SUCCESS: {
			if (!action.document?.id) {
				console.warn('DOCUMENT_SAVE_SUCCESS reducer');
                return state;
			}

			const documentRef = state.get(action.document?.id);
			if (!documentRef) {
				console.warn('Could not find document after call to save');
                return state;
			}

			st = state.clone();

			if (action.document.addedOn >= documentRef.addedOn) {
				st.set(action.document);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const document = new Document({...documentRef, persisted: true});
				st.set(document);
			}

			return updateStorage(st, [action.document]);
		}

		case Actions.DOCUMENT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ DOCUMENT_SAVE_MANY_SUCCESS reducer', action.documents.map(document => document.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const documents: Document[] = [];

			action.documents.forEach(e => {
				const documentRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!documentRef) console.warn('Could not find document after call to save');
				else if (e.addedOn >= documentRef.addedOn) {
				    st.set(e);
				    documents.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const document = new Document({...documentRef, persisted: true});
					st.set(document);
					documents.push(document);
				}
			});

			return updateStorage(st, documents);
		}

		case Actions.DOCUMENT_REMOVE_FAILURE: {
		    console.warn('DOCUMENT_REMOVE_FAILURE action', action.document?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.DOCUMENT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.DOCUMENT_RESTORE_MANY: {
			if (logging) console.debug('⮂ DOCUMENT_RESTORE_MANY reducer', action.documents.map(document => document.id).join(','));

			const toProcess = action.documents.map(document => new Document({...document}));
			st = state.clone();

			const updates: Document[] = [];

			toProcess.forEach(document => {
				const existing = st.get(document.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (document.addedOn >= existing.addedOn) {
						st.set(document);
						updates.push(document);
					}
					else if (logging) console.debug('⮂ DOCUMENT_RESTORE_MANY: skipping older version of document');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (document.status !== 'D') {
						st.set(document);
						updates.push(document);
					}
					else if (logging) console.debug('⮂ DOCUMENT_RESTORE_MANY: skipping deleted document not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DOCUMENT_REMOVE_MANY: {
			if (logging) console.debug('⮂ DOCUMENT_REMOVE_MANY reducer', action.documents.map(document => document.id).join(','));

			const toProcess = action.documents.map(document => new Document({...document}));
			st = state.clone();

			toProcess.forEach(document => {
				st.delete(document);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default documents;